import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bot, User, Plug, Code, Cpu, Layers } from 'lucide-react';
import { api, MarketplaceEntityDetail, EntityType } from '../../lib/api';
import { Badge } from '@/components/ui/badge';

/**
 * Get icon component for entity type
 */
function getEntityTypeIcon(type: EntityType) {
  switch (type) {
    case 'model-definition':
      return Bot;
    case 'specialized-model':
      return Cpu;
    case 'human':
      return User;
    case 'integration':
      return Plug;
    case 'code-block':
      return Code;
    case 'service':
      return Layers;
    default:
      return Bot;
  }
}

/**
 * Get display label for entity type
 */
function getEntityTypeLabel(type: EntityType): string {
  switch (type) {
    case 'model-definition':
      return 'Model';
    case 'specialized-model':
      return 'Specialized Model';
    case 'human':
      return 'Human Worker';
    case 'integration':
      return 'Integration';
    case 'code-block':
      return 'Code Block';
    case 'service':
      return 'Service';
    default:
      return type;
  }
}

/**
 * Get color class for entity type
 */
function getEntityTypeColorClass(type: EntityType): string {
  switch (type) {
    case 'model-definition':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'specialized-model':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'human':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'integration':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'code-block':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    case 'service':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200';
    default:
      return '';
  }
}

export function EntityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [entity, setEntity] = useState<MarketplaceEntityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchEntity = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await api.getMarketplaceEntity(id);
        setEntity(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load entity');
        setEntity(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEntity();
  }, [id]);

  if (loading) {
    return (
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-16 bg-muted rounded-lg mb-6" />
            <div className="h-8 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-full mb-2" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !entity) {
    return (
      <div className="py-16">
        <div className="max-w-lg mx-auto text-center px-4">
          <h1 className="text-2xl font-bold text-foreground mb-4">Entity Not Found</h1>
          <p className="text-muted-foreground mb-8">
            {error || "The entity you're looking for doesn't exist or has been removed."}
          </p>
          <Link
            to="/marketplace/browse?tab=entities"
            className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90"
          >
            Back to Entities
          </Link>
        </div>
      </div>
    );
  }

  const pricingLabel =
    entity.pricing.model === 'free'
      ? 'Free to use'
      : entity.pricing.model === 'fixed' && entity.pricing.priceCents
        ? `$${(entity.pricing.priceCents / 100).toFixed(2)} per use`
        : 'Variable pricing';

  const TypeIcon = getEntityTypeIcon(entity.type);
  const typeLabel = getEntityTypeLabel(entity.type);
  const typeColorClass = getEntityTypeColorClass(entity.type);

  return (
    <div>
      {/* Breadcrumb */}
      <div className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/marketplace/browse?tab=entities" className="hover:text-foreground">
              Entities
            </Link>
            <span>/</span>
            {entity.categorySlug && entity.categoryName && (
              <>
                <span className="hover:text-foreground">{entity.categoryName}</span>
                <span>/</span>
              </>
            )}
            <span className="text-foreground">{entity.name}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Entity Type Header */}
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg mb-6 ${typeColorClass}`}
            >
              <TypeIcon className="h-5 w-5" />
              <span className="font-medium">{typeLabel}</span>
            </div>

            <h1 className="text-3xl font-bold text-foreground mb-4">{entity.name}</h1>

            <p className="text-lg text-muted-foreground mb-6">{entity.description}</p>

            {/* Model Info for Model Definition Entities */}
            {entity.type === 'model-definition' && entity.modelProvider && entity.modelName && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-foreground mb-3">Model Information</h2>
                <div className="bg-muted rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-muted-foreground">Provider</span>
                      <p className="font-medium text-foreground capitalize">
                        {entity.modelProvider}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-muted-foreground">Model</span>
                      <p className="font-medium text-foreground">{entity.modelName}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="bg-card rounded-lg shadow-sm border border-border p-6 sticky top-8">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Pricing</span>
                  <span className="font-medium text-foreground">{pricingLabel}</span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Quality Score</span>
                  <span className="font-medium text-foreground">
                    {(entity.fitnessScore * 100).toFixed(0)}%
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Success Rate</span>
                  <span className="font-medium text-foreground">
                    {(entity.successRate * 100).toFixed(1)}%
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-border">
                  <span className="text-muted-foreground">Times Used</span>
                  <span className="font-medium text-foreground">
                    {entity.invocationCount.toLocaleString()}
                  </span>
                </div>

                {entity.avgLatencyMs && (
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Avg. Latency</span>
                    <span className="font-medium text-foreground">
                      {entity.avgLatencyMs < 1000
                        ? `${entity.avgLatencyMs}ms`
                        : `${(entity.avgLatencyMs / 1000).toFixed(1)}s`}
                    </span>
                  </div>
                )}

                {entity.categoryName && (
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-muted-foreground">Category</span>
                    <span className="font-medium text-foreground">{entity.categoryName}</span>
                  </div>
                )}

                <div className="flex justify-between items-center py-2">
                  <span className="text-muted-foreground">Type</span>
                  <Badge className={typeColorClass}>{typeLabel}</Badge>
                </div>

                {entity.publishedAt && (
                  <div className="flex justify-between items-center py-2 border-t border-border pt-4">
                    <span className="text-muted-foreground">Published</span>
                    <span className="font-medium text-foreground text-sm">
                      {new Date(entity.publishedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
